import React from 'react';
import _isEmpty from 'lodash-es/isEmpty';
import { useUser } from '@teetsh/app/src/containers/App/context/useUser';
import ManualClassroomsProvider from '@teetsh/app/src/containers/App/context/ClassroomsContext/ManualClassroomsContext';
import tw from 'twin.macro';
import { ROItemList } from '@teetsh/app/src/components/ROItemList';
import { CJSeanceSearchResult } from '@teetsh/app/src/containers/Search/types';
import { Tag } from '@teetsh/app/src/components/Tag';
import HtmlViewer from '@teetsh/app/src/components/HtmlViewer';
import { getSeanceAttachmentUrl } from '@teetsh/app/src/utils/urls';
import { getLinkedFdpLinkUrl } from 'containers/CahierJournal/DayColumn/Seance/CJEventBody/Activities';
import { format } from '@teetsh/app/src/utils/time/format';
import { getDurationInHoursAndMinutesBetween } from '@teetsh/app/src/utils/time';
import { DocumentTextIcon as DocumentText } from '@heroicons/react/24/outline';
import { BookOpenIcon as BookOpen } from '@heroicons/react/24/outline';
import { DocumentArrowDownIcon as DocumentDownload } from '@heroicons/react/24/outline';
import { Bars3BottomLeftIcon as MenuAlt2 } from '@heroicons/react/20/solid';
import { PaperClipIcon as PaperClip } from '@heroicons/react/24/outline';
import { ExternalOrInternalLink } from 'containers/CahierJournal/DayColumn/Seance/CJEventBody/ExternalOrInternalLink';
import { DocumentIcon as Document } from '@heroicons/react/24/outline';
import { useToggleSearchModal } from '@teetsh/app/src/containers/Search/atom';
import Line from '@teetsh/app/src/containers/Search/components/Line';
import { BriefcaseIcon as Briefcase } from '@heroicons/react/24/outline';
import { Bars4Icon as ViewList } from '@heroicons/react/24/outline';
import { BILAN_EVENT, CLASSIC_EVENT, RAPPEL_EVENT } from 'containers/constants';
import { useTranslation } from 'react-i18next';
import { PreviewCustomFieldList } from '../components/PreviewCustomField';

interface Props {
  seance: CJSeanceSearchResult;
}

export function SeancePreview(props: Props) {
  const { seance } = props;
  const { boLabels, id: userId, locale } = useUser();
  const toggleSearchModal = useToggleSearchModal();
  const { t } = useTranslation(['common']);
  const timeFormat = t('common:time.pattern_HH_mm');

  const formatedDuration = getDurationInHoursAndMinutesBetween(
    seance.start,
    seance.end,
  );
  const isBoEmpty =
    seance?.bo?.matiere?.length === 0 &&
    seance?.bo?.domaine?.length === 0 &&
    seance?.bo?.competence?.length === 0 &&
    seance?.bo?.goals?.length === 0;
  return (
    <div tw="p-6">
      <ManualClassroomsProvider classrooms={[]}>
        <div>
          <div tw="flex items-center">
            <div tw="text-xl font-bold flex-grow">{seance.title}</div>
            {seance.tag && (
              <div tw="text-right shrink-0">
                {seance.type === CLASSIC_EVENT && (
                  <Tag color={seance.tag?.color}>{seance.tag?.title}</Tag>
                )}
                {seance.type === BILAN_EVENT && (
                  <Tag color="black">
                    {seance?.title !== ''
                      ? seance?.title
                      : t('common:search.results.bilan')}
                  </Tag>
                )}
                {seance.type === RAPPEL_EVENT && (
                  <Tag color="black">
                    {seance?.title !== ''
                      ? seance?.title
                      : t('common:search.results.rappels')}
                  </Tag>
                )}
              </div>
            )}
          </div>
          <div tw="space-x-3 text-sm font-normal">
            <span>{format(seance.start, locale, 'eeee dd MMMM yyyy')}</span>
            {seance.type === CLASSIC_EVENT && (
              <span>
                {format(seance.start, locale, timeFormat)} -{' '}
                {format(seance.end, locale, timeFormat)}
                <span tw="ml-2 text-muted">({`${formatedDuration}`})</span>
              </span>
            )}
          </div>
        </div>
        <div tw="border-b w-full my-4" />

        <div tw="grid grid-cols-12 gap-x-2 gap-y-4">
          {!isBoEmpty && (
            <Line icon={BookOpen}>
              <div tw="space-y-1">
                <ROItemList
                  label={boLabels.matiereSingularName}
                  labelPlural={boLabels.matierePluralName}
                  items={(seance.bo.matiere || []).map(m => m.value)}
                />
                <ROItemList
                  label={boLabels.domaineSingularName}
                  labelPlural={boLabels.domainePluralName}
                  items={(seance.bo.domaine || []).map(m => m.value)}
                />
                <ROItemList
                  label={boLabels.competenceSingularName}
                  labelPlural={boLabels.competencePluralName}
                  items={(seance.bo.competence || []).map(m => m.value)}
                />
                <ROItemList
                  label={boLabels.objectifSingularName}
                  labelPlural={boLabels.objectifPluralName}
                  items={(seance.bo.goals || []).map(m => m.value)}
                />
              </div>
            </Line>
          )}
          {(seance.customFields || []).length > 0 && (
            <Line icon={ViewList}>
              <PreviewCustomFieldList
                customFields={seance.customFields}
                customFieldDefinitions={undefined} // TODO pass on the definitions - hard to get from here
              />
            </Line>
          )}

          {(seance.materials || []).length > 0 && (
            <Line icon={Briefcase}>
              <ROItemList
                items={seance.materials.map(m => m.value)}
                label={t('common:search.results.material_one')}
                labelPlural={t('common:search.results.material_other')}
              />
            </Line>
          )}

          {seance.linkedFichesDePrep.length > 0 && (
            <Line icon={DocumentText}>
              {seance.linkedFichesDePrep
                .filter(a => typeof a.label === 'string')
                .map(a => (
                  <ExternalOrInternalLink
                    onClick={() => toggleSearchModal(false)}
                    internalLink={getLinkedFdpLinkUrl({
                      print: false,
                      sequenceId: a.sequenceId,
                      fdpId: a.id,
                      userId,
                    })}
                  >
                    <div
                      key={a.label}
                      tw="rounded-xl bg-neutral-quiet hover:bg-neutral-moderate p-2 py-1 inline-flex text-xs items-center cursor-pointer text-neutral-moderate font-medium"
                    >
                      <Document tw="h-4 w-4 mr-1" />
                      {a.label}
                    </div>
                  </ExternalOrInternalLink>
                ))}
            </Line>
          )}
          {(seance?.attachments || []).length > 0 && (
            <Line icon={PaperClip}>
              <div tw="space-x-2">
                {seance.attachments.map(a => (
                  <a
                    onClick={e => e.stopPropagation()}
                    href={getSeanceAttachmentUrl(
                      a.s3Path,
                      a.s3Filename,
                      a.label,
                    )}
                    rel="noreferrer noopener"
                    css={[
                      tw`text-neutral-moderate hover:underline break-all font-medium`,
                    ]}
                    target="_blank"
                  >
                    <div
                      key={a.label}
                      tw="rounded-xl bg-neutral-quiet hover:bg-neutral-moderate p-2 py-1 inline-flex text-xs items-center cursor-pointer"
                    >
                      <DocumentDownload tw="h-4 w-4 mr-1 text-neutral-moderate" />
                      {a.label}
                    </div>
                  </a>
                ))}
              </div>
            </Line>
          )}

          {!_isEmpty(seance.flow) && (
            <Line icon={MenuAlt2}>
              <HtmlViewer onChange={() => {}} text={seance.flow} />
            </Line>
          )}
        </div>
      </ManualClassroomsProvider>
    </div>
  );
}
